import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface TutorialsListProps {
  activeCategory: string;
}

const TutorialsList = ({ activeCategory }: TutorialsListProps) => {
  const { t } = useTranslation();

  const tutorials = [
    {
      id: 'pdf-to-word',
      category: 'pdf-convert',
      title: t('tutorials:pdfToWord.title'),
      description: t('tutorials:pdfToWord.description'),
      icon: 'ri-file-word-line',
      color: 'from-blue-500 to-blue-600',
      link: '/pdf-to-word',
      steps: [
        {
          number: 1,
          title: t('tutorials:pdfToWord.step1Title'),
          description: t('tutorials:pdfToWord.step1'),
          icon: 'ri-upload-cloud-line'
        },
        {
          number: 2,
          title: t('tutorials:pdfToWord.step2Title'),
          description: t('tutorials:pdfToWord.step2'),
          icon: 'ri-refresh-line'
        },
        {
          number: 3,
          title: t('tutorials:pdfToWord.step3Title'),
          description: t('tutorials:pdfToWord.step3'),
          icon: 'ri-download-cloud-line'
        }
      ]
    },
    {
      id: 'pdf-to-excel',
      category: 'pdf-convert',
      title: t('tutorials:pdfToExcel.title'),
      description: t('tutorials:pdfToExcel.description'),
      icon: 'ri-file-excel-line',
      color: 'from-green-500 to-green-600',
      link: '/pdf-to-excel',
      steps: [
        {
          number: 1,
          title: t('tutorials:pdfToExcel.step1Title'),
          description: t('tutorials:pdfToExcel.step1'),
          icon: 'ri-upload-cloud-line'
        },
        {
          number: 2,
          title: t('tutorials:pdfToExcel.step2Title'),
          description: t('tutorials:pdfToExcel.step2'),
          icon: 'ri-refresh-line'
        },
        {
          number: 3,
          title: t('tutorials:pdfToExcel.step3Title'),
          description: t('tutorials:pdfToExcel.step3'),
          icon: 'ri-download-cloud-line'
        }
      ]
    },
    {
      id: 'pdf-to-ppt',
      category: 'pdf-convert',
      title: t('tutorials:pdfToPpt.title'),
      description: t('tutorials:pdfToPpt.description'),
      icon: 'ri-file-ppt-line',
      color: 'from-orange-500 to-orange-600',
      link: '/pdf-to-ppt',
      steps: [
        {
          number: 1,
          title: t('tutorials:pdfToPpt.step1Title'),
          description: t('tutorials:pdfToPpt.step1'),
          icon: 'ri-upload-cloud-line'
        },
        {
          number: 2,
          title: t('tutorials:pdfToPpt.step2Title'),
          description: t('tutorials:pdfToPpt.step2'),
          icon: 'ri-refresh-line'
        },
        {
          number: 3,
          title: t('tutorials:pdfToPpt.step3Title'),
          description: t('tutorials:pdfToPpt.step3'),
          icon: 'ri-download-cloud-line'
        }
      ]
    },
    {
      id: 'image-convert',
      category: 'image-convert',
      title: t('tutorials:imageConvert.title'),
      description: t('tutorials:imageConvert.description'),
      icon: 'ri-image-line',
      color: 'from-purple-500 to-purple-600',
      link: '/image-convert',
      steps: [
        {
          number: 1,
          title: t('tutorials:imageConvert.step1Title'),
          description: t('tutorials:imageConvert.step1'),
          icon: 'ri-upload-cloud-line'
        },
        {
          number: 2,
          title: t('tutorials:imageConvert.step2Title'),
          description: t('tutorials:imageConvert.step2'),
          icon: 'ri-settings-3-line'
        },
        {
          number: 3,
          title: t('tutorials:imageConvert.step3Title'),
          description: t('tutorials:imageConvert.step3'),
          icon: 'ri-download-cloud-line'
        }
      ]
    },
    {
      id: 'pdf-tools',
      category: 'pdf-tools',
      title: t('tutorials:pdfTools.title'),
      description: t('tutorials:pdfTools.description'),
      icon: 'ri-tools-line',
      color: 'from-teal-500 to-teal-600',
      link: '/pdf-tools',
      steps: [
        {
          number: 1,
          title: t('tutorials:pdfTools.step1Title'),
          description: t('tutorials:pdfTools.step1'),
          icon: 'ri-upload-cloud-line'
        },
        {
          number: 2,
          title: t('tutorials:pdfTools.step2Title'),
          description: t('tutorials:pdfTools.step2'),
          icon: 'ri-compasses-2-line'
        },
        {
          number: 3,
          title: t('tutorials:pdfTools.step3Title'),
          description: t('tutorials:pdfTools.step3'),
          icon: 'ri-download-cloud-line'
        }
      ]
    }
  ];

  const filteredTutorials = activeCategory === 'all' 
    ? tutorials 
    : tutorials.filter(t => t.category === activeCategory);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12">
          {filteredTutorials.map((tutorial) => (
            <article key={tutorial.id} className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300">
              {/* Tutorial Header */}
              <div className={`bg-gradient-to-r ${tutorial.color} p-8 text-white`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <i className={`${tutorial.icon} text-4xl`}></i>
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold font-['Space_Grotesk']">{tutorial.title}</h2>
                    <p className="text-white/90 text-lg mt-1">{tutorial.description}</p>
                  </div>
                </div>
              </div>

              {/* Tutorial Steps */}
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {tutorial.steps.map((step, index) => (
                    <div key={index} className="relative">
                      {/* Step Number Badge */}
                      <div className="absolute -top-3 -left-3 w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg z-10">
                        {step.number}
                      </div>

                      {/* Step Card */}
                      <div className="bg-gray-50 rounded-xl p-6 pt-8 h-full border-2 border-gray-100 hover:border-teal-300 transition-all">
                        <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center mb-4">
                          <i className={`${step.icon} text-3xl text-teal-600`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3 font-['Space_Grotesk']">
                          {step.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {step.description}
                        </p>
                      </div>

                      {/* Arrow Connector (hidden on last step and mobile) */}
                      {index < tutorial.steps.length - 1 && (
                        <div className="hidden md:block absolute top-1/2 -right-8 transform -translate-y-1/2 z-0">
                          <i className="ri-arrow-right-line text-4xl text-teal-300"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <div className="mt-8 text-center">
                  <Link
                    to={tutorial.link}
                    className={`inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r ${tutorial.color} text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer`}
                  >
                    <span>{t('tutorials:tryNow')}</span>
                    <i className="ri-arrow-right-line text-2xl"></i>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center bg-gradient-to-br from-teal-50 to-orange-50 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 font-['Space_Grotesk']">
            {t('tutorials:bottomCTA.title')}
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {t('tutorials:bottomCTA.description')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-bold text-lg hover:shadow-xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line text-2xl"></i>
            <span>{t('tutorials:bottomCTA.button')}</span>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default TutorialsList;